import type { RemindMeRequest, UnremindMeRequest } from "@/modules/eventDetails/client/api/types"
import type { FunctionComponent } from "react"

import { useEffect } from "react"

import IconBell from "@jobteaser/ui-kit/assets/icons/jds_bell.svg"
import IconBellFull from "@jobteaser/ui-kit/assets/icons/jds_bellFull.svg"
import IconHeart from "@jobteaser/ui-kit/assets/icons/jds_heart.svg"
import IconHeartFull from "@jobteaser/ui-kit/assets/icons/jds_heartFull.svg"
import { Button, BUTTON_VARIANT } from "@jobteaser/ui-kit/components/Button"

import { SAVE, SAVED } from "@/modules/eventDetails/client/constants"
import {
  EVENT_DETAILS_REMIND_ME_UNLEASH_EXPERIMENT,
  EVENT_DETAILS_REMIND_ME_UNLEASH_EXPERIMENT_VARIANTS,
} from "@/modules/eventDetails/client/constants/flags"
import { useEventDetailsData } from "@/modules/eventDetails/client/hooks/useEventDetailsData"
import { useRegistrationStatus } from "@/modules/eventDetails/client/hooks/useRegistrationStatus"
import { useRemindMe } from "@/modules/eventDetails/client/hooks/useRemindMe"
import { useUnremindMe } from "@/modules/eventDetails/client/hooks/useUnremindMe"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { useNotification } from "@/modules/notification/hooks"
import { useTrackEvent } from "@/modules/tracking/useTrackEvent"
import { useFlag } from "@/modules/unleash/hooks/useFlag"

export const SaveButton: FunctionComponent = () => {
  const { t } = useTranslation(["event_details", "shared_error"])
  const locale = useLocale()
  const hostname = typeof window !== "undefined" ? window.location.host : ""
  const [subdomain] = hostname.split(".")
  const event = useEventDetailsData()
  const { registrationStatus, refetch: refetchRegistrationStatus } = useRegistrationStatus({
    eventId: event?.id || "",
    locale,
  })

  const trackEvent = useTrackEvent()

  const { mutate: getRemindMe, isPending: isRemindMeLoading } = useRemindMe()
  const { mutate: getUnremindMe, isPending: isUnremindMeLoading } = useUnremindMe()
  const showNotification = useNotification()
  const isRemindMeButtonVisible = useFlag(
    EVENT_DETAILS_REMIND_ME_UNLEASH_EXPERIMENT,
    EVENT_DETAILS_REMIND_ME_UNLEASH_EXPERIMENT_VARIANTS.NEW_REMIND_ME
  )

  useEffect(() => {
    if (event && registrationStatus && !registrationStatus.isSaved) {
      trackEvent("event_ad_save_display", {
        event_id: event.id,
        is_visible: true,
        service: "event_details",
        subdomain,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrationStatus?.isSaved])

  if (!registrationStatus || !event) {
    return null
  }

  const handleSaveButtonClick = (): void => {
    getRemindMe(
      {
        hostname,
        id: event.id,
        locale,
      } as RemindMeRequest,
      {
        onError: () => {
          showNotification({
            duration: 10000,
            title: t("shared_error.generic"), // TODO: change title
            variant: "error",
          })
        },
        onSuccess: () => {
          trackEvent("event_ad_save", {
            context: "event_ad",
            event_ad_id: event.id,
            service: "event_details",
          })
          refetchRegistrationStatus()
        },
      }
    )
  }

  const handleRemoveFromSavedButtonClick = (): void => {
    getUnremindMe(
      {
        hostname,
        id: event.id,
        locale,
      } as UnremindMeRequest,
      {
        onError: () => {
          showNotification({
            duration: 10000,
            title: t("shared_error.generic"), // TODO: change title
            variant: "error",
          })
        },
        onSuccess: () => {
          refetchRegistrationStatus()
        },
      }
    )
  }

  return (
    <>
      {isRemindMeButtonVisible && (
        <Button
          icon={registrationStatus.isSaved ? IconBellFull : IconBell}
          variant={BUTTON_VARIANT.GHOST}
          onClick={registrationStatus.isSaved ? handleRemoveFromSavedButtonClick : handleSaveButtonClick}
          disabled={isRemindMeLoading || isUnremindMeLoading}
          data-testid={`event-details-registration-details-${registrationStatus.isSaved ? SAVED : SAVE}-button`}
        >
          {t("event_details.button.remind_me")}
        </Button>
      )}
      {!isRemindMeButtonVisible && (
        <Button
          icon={registrationStatus.isSaved ? IconHeartFull : IconHeart}
          variant={BUTTON_VARIANT.GHOST}
          onClick={registrationStatus.isSaved ? handleRemoveFromSavedButtonClick : handleSaveButtonClick}
          disabled={isRemindMeLoading || isUnremindMeLoading}
          data-testid={`event-details-registration-details-${registrationStatus.isSaved ? SAVED : SAVE}-button`}
        >
          {t(`event_details.button.${registrationStatus.isSaved ? SAVED : SAVE}`)}
        </Button>
      )}
    </>
  )
}
